import classNames from 'classnames'
import { isEmpty } from 'lodash'
import { map, values } from 'lodash/fp'
import DeleteIcon from 'mdi-react/DeleteIcon'
import React, { useCallback, useMemo } from 'react'
import { useMuiSelectUtils } from 'scenes/Dashboard/components-next/Inputs/Select/Select.hook'
import { SignalLogic, SignalLogicTemplates, SignalsWithFounder } from 'scenes/Dashboard/data/logic'
import { SignalAssessment } from 'scenes/Dashboard/data/signalAssessments/types'
import { connect, FormProps, getIn } from 'utils-next/forms'
import * as Yup from 'yup'

import { createStyles, IconButton, MenuItem, Paper, Select, SelectChangeEvent, Theme, Typography } from '@mui/material'
import { WithStyles, withStyles } from '@mui/styles'

import { OrganizationFormValues } from '../../OrganizationDetailsEdit.models'
import EditorField from './components/EditorField'

type OuterSignalProps = {
  className?: string
  index: number
  onRemoveItem: () => void
  signal: SignalAssessment
  founders: string[]
}

type SignalProps = OuterSignalProps & WithStyles<typeof styles> & { formik: FormProps<OrganizationFormValues> }

const getTemplate = (founders: string[], signal: string) => {
  let template = ''
  if (founders.length && SignalsWithFounder.includes(signal)) {
    template += founders.reduce((accumulated, founder) => `${accumulated}${founder}<br>`, '') + '<br>'
  }

  const signalTemplate = SignalLogicTemplates[signal]
  if (signalTemplate && signalTemplate.length) {
    template += signalTemplate.reduce((accumulated: string, templateRow: string) => `${accumulated}<br>${templateRow}`)
  }

  return template
}

const getSummary = (founders: string[], signalAssessment: SignalAssessment) => {
  if (signalAssessment.assessmentSupportiveData?.summary) {
    return signalAssessment.assessmentSupportiveData.summary
  }
  if (signalAssessment.signalLogic) {
    return getTemplate(founders, signalAssessment.signalLogic)
  }

  return ''
}
type SignalLogicOptionType = {
  value: string
  text: string
}
function Signal({ classes, className, formik, index, onRemoveItem, signal, founders }: SignalProps) {
  const signalLogicArray = useMemo(
    () => map((x) => ({ value: x, text: x }))(values(SignalLogic)),
    []
  ) as SignalLogicOptionType[]

  const name = `signalAssessmentsValues[${index}]`

  const { getItem, setSelectedItem, selectedItem } = useMuiSelectUtils(signalLogicArray, signal.signalLogic || '')
  const onLogicChange = useCallback(
    (event: SelectChangeEvent<{ value: unknown }>) => {
      formik.setFieldValue(`${name}.signalLogic`, event.target.value as string)
      const template = getTemplate(founders, event.target.value as string)
      formik.setFieldValue(`${name}.assessmentSupportiveData.summary`, template)
      setSelectedItem(event.target.value as string)
    },
    [formik, name]
  )

  const onSummaryChange = useCallback(
    (html: string) => formik.setFieldValue(`${name}.assessmentSupportiveData.summary`, html),
    [formik, name]
  )

  const error = getIn(formik.errors, name)
  const summary = getSummary(founders, signal)

  return (
    <Paper elevation={2} className={classNames(className, classes.container)}>
      <EditorField label="Summary" onChange={onSummaryChange} value={summary} />

      <Typography className={classes.label} variant="caption">
        Select a signal logic
      </Typography>

      <Select
        variant="standard"
        value={getItem(selectedItem)}
        onChange={onLogicChange}
        disabled={formik.isSubmitting}
        renderValue={() => getItem(selectedItem)?.text}
      >
        {signalLogicArray.map((signalLogic) => (
          <MenuItem key={signalLogic.value} value={signalLogic.value}>
            {signalLogic.text}
          </MenuItem>
        ))}
      </Select>

      <footer className={classes.footer}>
        <div className={classes.error}>{error || ''}</div>
        <IconButton aria-label="Delete signal assessment" onClick={onRemoveItem}>
          <DeleteIcon />
        </IconButton>
      </footer>
    </Paper>
  )
}

const styles = ({ spacing, palette }: Theme) =>
  createStyles({
    container: {
      padding: `${spacing(2)}px ${spacing(3)}px`,
    },
    label: {
      display: 'block',
      marginBottom: spacing(1),
    },
    error: {
      color: palette.extra.negative,
      fontWeight: 500,
    },
    footer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
  })

export const signalAssessmentValidationSchema = Yup.mixed().test(
  'has-summary-or-description',
  'Summary and description cannot be empty at the same time',
  function ({ assessmentSupportiveData }: SignalAssessment) {
    if (
      !assessmentSupportiveData ||
      (isEmpty(assessmentSupportiveData.summary) && isEmpty(assessmentSupportiveData.description))
    ) {
      return false
    }
    return true
  } as any
)

export default connect<OuterSignalProps, OrganizationFormValues>(withStyles(styles)(Signal))
